/**
 * ledger — Sửa dữ liệu cũ theo yêu cầu của người dùng (màn Kiểm tra toàn vẹn). Một transaction, chỉ thêm trường còn thiếu.
 */
import { getFirestore, doc, runTransaction, writeBatch, Timestamp } from 'firebase/firestore';
import { APP_ID } from '../config/constants';
import { isQuotaError, QUOTA_MESSAGE } from './errors';
import { LedgerError, type LedgerResult } from './ledger';

/**
 * Ví loại ngân hàng/tiền mặt/… bản ghi cũ thiếu trường `balance` thì luật Firestore từ chối mọi lần sửa không ghi số dư
 * (đổi tên, ngừng dùng…). Đặt balance = 0: đúng bằng giá trị đang được tính (thiếu = 0), nên KHÔNG làm đổi số liệu nào.
 */
export const initMissingBalances = async (userId: string, walletIds: string[]): Promise<LedgerResult> => {
  try {
    if (walletIds.length === 0) throw new LedgerError('Chưa chọn ví nào.');
    const db = getFirestore();
    let count = 0;
    await runTransaction(db, async (tx) => {
      count = 0;
      const now = Timestamp.now();
      const refs = walletIds.map(id => doc(db, 'artifacts', APP_ID, 'users', userId, 'sources', id));
      const snaps = await Promise.all(refs.map(r => tx.get(r)));
      snaps.forEach((s, i) => {
        const d = s.exists() ? (s.data() as Record<string, any>) : null;
        if (d && d.type !== 'credit' && typeof d.balance !== 'number') { tx.update(refs[i], { balance: 0, updatedAt: now }); count++; }
      });
    });
    return { success: true, count };
  } catch (e) {
    if (e instanceof LedgerError) return { success: false, error: e.message };
    if (isQuotaError(e)) return { success: false, error: QUOTA_MESSAGE };
    return { success: false, error: `Không ghi được (chưa có gì thay đổi): ${(e as Error).message}` };
  }
};

export interface LoanTermsInput {
  /** %/năm sau khi đổi */
  interestRate: number;
  /** yyyy-mm-dd: ngày áp dụng lãi suất mới (chỉ để ghi lịch sử, không tính lại kỳ đã trả) */
  effectiveDate: string;
  /** Số trả mỗi tháng mới (bỏ trống = giữ nguyên) */
  monthlyPayment?: number | null;
  /** Mẫu định kỳ "trả nợ" của khoản vay này cần đổi số tiền theo (bỏ trống = không đổi mẫu) */
  templateIds?: string[];
}

/**
 * Cập nhật lãi suất (và số trả hằng tháng) của một khoản vay. Lãi từ nay tính theo lãi suất mới; các lần trả đã ghi KHÔNG đổi.
 * Lưu vết `rateHistory` ngay trên khoản vay để biết lãi suất đã đổi khi nào.
 */
export const updateLoanTerms = async (userId: string, debtId: string, input: LoanTermsInput): Promise<LedgerResult> => {
  try {
    const rate = Number(input.interestRate);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) throw new LedgerError('Lãi suất phải từ 0 đến 100 %/năm.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(input.effectiveDate)) throw new LedgerError('Chọn ngày áp dụng lãi suất mới.');
    const monthly = input.monthlyPayment == null ? null : Math.round(Number(input.monthlyPayment));
    if (monthly !== null && !(monthly > 0)) throw new LedgerError('Số trả mỗi tháng phải lớn hơn 0.');
    const db = getFirestore();
    const dRef = doc(db, 'artifacts', APP_ID, 'users', userId, 'debts', debtId);
    const tRefs = (input.templateIds ?? []).map(id => doc(db, 'artifacts', APP_ID, 'users', userId, 'recurringTemplates', id));
    let templates = 0;
    await runTransaction(db, async (tx) => {
      templates = 0;
      const now = Timestamp.now();
      const dSnap = await tx.get(dRef);
      if (!dSnap.exists()) throw new LedgerError('Không tìm thấy khoản vay.');
      const debt = dSnap.data() as Record<string, any>;
      const tSnaps = await Promise.all(tRefs.map(r => tx.get(r)));
      const history = Array.isArray(debt.rateHistory) ? debt.rateHistory : [];
      const patch: Record<string, any> = {
        interestRate: rate, updatedAt: now,
        rateHistory: [...history, { from: input.effectiveDate, rate, previousRate: Number(debt.interestRate) || 0, ...(monthly !== null ? { monthlyPayment: monthly } : {}) }].slice(-30)
      };
      if (monthly !== null) patch.monthlyPayment = monthly;
      tx.update(dRef, patch);
      if (monthly !== null) tSnaps.forEach((s, i) => {
        const t = s.exists() ? (s.data() as Record<string, any>) : null;
        if (t && t.type === 'payment' && t.paymentType === 'debt' && t.debtId === debtId) { tx.update(tRefs[i], { amount: monthly, updatedAt: now }); templates++; }
      });
    });
    return { success: true, count: templates };
  } catch (e) {
    if (e instanceof LedgerError) return { success: false, error: e.message };
    if (isQuotaError(e)) return { success: false, error: QUOTA_MESSAGE };
    return { success: false, error: `Không ghi được (chưa có gì thay đổi): ${(e as Error).message}` };
  }
};

/**
 * Đánh dấu các khoản chi "không phải trùng" để cảnh báo ghi trùng không hiện lại cho nhóm đó.
 * Chỉ thêm cờ `notDuplicate` — không đổi số tiền, ví hay số dư.
 */
export const markNotDuplicate = async (userId: string, expenseIds: string[]): Promise<LedgerResult> => {
  try {
    if (!expenseIds.length) return { success: true };
    const db = getFirestore();
    const batch = writeBatch(db);
    const now = Timestamp.now();
    for (const id of expenseIds) batch.update(doc(db, 'artifacts', APP_ID, 'users', userId, 'expenses', id), { notDuplicate: true, updatedAt: now });
    await batch.commit();
    return { success: true };
  } catch (e) {
    return { success: false, error: isQuotaError(e) ? QUOTA_MESSAGE : (e as Error).message };
  }
};
